import {
  type AudioStreamStartResult,
  type OnAudioDataAvailable,
  AUDIO_BLOB_MILLIS,
  MediaRecorderMeta,
  WEBM_PCM_MIME_TYPE
} from "../models/AudioStream.interface";
import { getLatestHalfSecondRawStream, initRawStreamAnalyzer } from "./rawStreamSetup";

/**
 * Create a MediaRecorder, or return an error.
 *
 * If a mediaRecorder is created, the ondataavailable callback will fire every 500ms
 * with additional Blob format audio data.
 */
export async function recordNewAudioStreamStart(
  onDataAvailable: OnAudioDataAvailable,
  meta: MediaRecorderMeta
): Promise<AudioStreamStartResult> {
  (navigator.mediaSession as any)?.setMicrophoneActive?.(true);
  if (!navigator.mediaDevices?.getUserMedia) {
    return Promise.resolve({
      error:
        "This browser does not support recording.  If you can't switch browsers, try file upload instead (your device may prompt you to use a separate recording app).",
    });
  }
  const audioCtx = new AudioContext({ sampleRate: 48000 });

  return navigator.mediaDevices
    .getUserMedia({ audio: { sampleRate: 48000, advanced: [{ noiseSuppression: true, channelCount: 1 }] } }) // this does not work: { advanced: [{ sampleRate: 8192 }] } })
    .then((stream) => {
      const mediaRecorder = new MediaRecorder(stream, { mimeType: meta.streamFormat === "WEBM_PCM" ? WEBM_PCM_MIME_TYPE : undefined });
      const source = audioCtx.createMediaStreamSource(mediaRecorder.stream);
      const analyzer = audioCtx.createAnalyser();
      if (meta.streamFormat === "RAW") {
        initRawStreamAnalyzer(analyzer);
        source.connect(analyzer);
      }
      source.connect(analyzer);
      mediaRecorder.ondataavailable = async (e: BlobEvent) => {
        let rawBlob = undefined;
        if (meta.streamFormat === "RAW") {
          rawBlob = getLatestHalfSecondRawStream(analyzer);
        }
        onDataAvailable({ pcmBlob: e.data, rawBlob, sampleRate: audioCtx.sampleRate });
      };
      mediaRecorder.onerror = (e: any) => {
        console.warn(`mediaRecorder id=${meta.id} error=${e}`);
      };
      mediaRecorder.start(AUDIO_BLOB_MILLIS);
      return { mediaRecorder };
    })
    .catch((err) => {
      return {
        error: `getUserMedia error: ${err}`,
      };
    });
}

export function recordAudioStreamStop(recorder?: MediaRecorder) {
  if (recorder && recorder?.state !== "inactive") {
    recorder.stop();
    // chrome doesn't remove the recording favicon from the title bar unless we also do this:
    recorder.stream?.getAudioTracks?.().forEach?.((at) => at?.stop?.());
  }
  (navigator.mediaSession as any)?.setMicrophoneActive?.(false);
}
